import Table from '@eduzz/houston-ui/Table';
import Typography from '@eduzz/houston-ui/Typography';
import clsx from 'clsx';
import Alert from 'components/Alert';
import CalculateCommissionAffiliate from 'components/CalculateCommissionAffiliate';
import React, { useState } from 'react';
import { FormattedNumber } from 'react-intl';
import { useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useCallbackObservable, useObservable } from 'react-use-observable';
import { tap } from 'rxjs/operators';
import PermissionHide from 'v2/components/Shared/PermissionHide';
import Truncate from 'v2/components/Shared/TruncateText';
import { EStatusAffiliation } from 'v2/interfaces/affiliation';
import affiliationsService from 'v2/services/affiliations';

import type { IAffiliation } from 'v2/interfaces/affiliation';

import RecruitmentCommission from '../RecruitmentCommission';
import { useStyles } from './styles';

export const List = () => {
  const classes = useStyles();

  const history = useHistory();

  const [isLoading] = useObservable(() => affiliationsService.isLoading(), []);
  const [affiliations] = useObservable(() => affiliationsService.getAffiliations(), []);
  const [pagination] = useObservable(() => affiliationsService.getPaginationResponse(), []);
  const [loadProductById] = useCallbackObservable((id: number) => affiliationsService.loadProductById(id), []);
  const [cancelAffiliation] = useCallbackObservable(
    (affiliation: IAffiliation) =>
      affiliationsService.cancelAffilation(affiliation).pipe(
        tap(() => {
          toast.success('Afiliação cancelada com sucesso');
        }, toast.error)
      ),
    []
  );

  const [isModalRecruitmentOpen, setIsModalRecruitmentOpen] = useState<boolean>(false);

  const onChangeRowsPerPage = (per_page: number) => {
    affiliationsService.setPagination({ ...pagination, per_page, current_page: 1 });
    document.getElementById('affiliations-table').scrollIntoView();
  };

  const onChangePage = (page: number) => {
    affiliationsService.setPagination({ ...pagination, current_page: page });
  };

  const handleClickShare = (affiliation: IAffiliation) => {
    history.push(`/producer/campaigns/${affiliation.product_id}`);
  };

  const handleRecruitmentClick = (affiliation: IAffiliation) => {
    loadProductById(affiliation.product_id);
    setIsModalRecruitmentOpen(true);
  };

  const closeModalRecruitment = () => {
    setIsModalRecruitmentOpen(false);
  };

  const handleClickCancelAffiliation = async (affiliation: IAffiliation) => {
    const confirmation = await Alert.show({
      title: 'Atenção',
      message: 'Deseja realmente cancelar a afiliação?',
      confirmation: true,
      buttonOk: 'Confirmar',
      buttonCancel: 'Cancelar',
    });
    if (!confirmation) {
      return;
    }
    cancelAffiliation(affiliation);
  };

  const statusAffiliaton = {
    P: 'Pendente',
    A: 'Aprovada',
    R: 'Reprovada',
  };

  return (
    <>
      <Table id='affiliations-table' total={pagination?.total_items || 0} loading={isLoading}>
        <Table.Header>
          <Table.Column width={100}>Status</Table.Column>
          <Table.Column width={300}>Produto</Table.Column>
          <Table.Column width={200}>Produtor</Table.Column>
          <Table.Column width={120}>Recrutamento</Table.Column>
          <Table.Column width={90} align='right'>
            Recrutados
          </Table.Column>
          <Table.Column width={140} align='right'>
            Preço
          </Table.Column>
          <Table.Column width={250} align='right'>
            Comissão
          </Table.Column>
        </Table.Header>

        <Table.Body>
          {affiliations?.map((affiliation, index) => (
            <Table.Row key={affiliation.id} data={affiliation} index={index}>
              <Table.Cell>
                <Typography size='xxxs' weight='bold' className={clsx(classes.affiliationStatusCell, `--${affiliation.affiliations_status}`)}>
                  {statusAffiliaton[affiliation.affiliations_status]}
                </Typography>
              </Table.Cell>
              <Table.Cell>
                <Typography size='xxs' weight='bold' className={classes.productTitle}>
                  {affiliation.product_title}
                </Typography>
                <Typography size='xxs'>({affiliation.product_id})</Typography>
                {affiliation.product_status === 'inactive' && <span className={classes.inactiveTag}>Inativo</span>}
              </Table.Cell>
              <Table.Cell>
                <a href={`/producer/detail/${affiliation.producer.id}`} target='_blank' rel='noopener noreferrer'>
                  <Truncate count={25} final='...'>
                    {affiliation.producer.business_name}
                  </Truncate>
                </a>
              </Table.Cell>
              <Table.Cell>
                <p className={clsx(classes.affiliationRecruitmentCell, affiliation.recruitment ? '--enabled' : '--disabled')}>
                  {affiliation.recruitment ? 'Habilitado' : 'Desabilitado'}
                </p>
              </Table.Cell>
              <Table.Cell align='right'>{affiliation.indications}</Table.Cell>
              <Table.Cell align='right'>
                <PermissionHide inverse action='myeduzz:meus-produtos:ocultar-valores' fallback={<em>oculto</em>}>
                  {affiliation.has_variations ? (
                    <div>
                      <span>máx.: </span>
                      <FormattedNumber value={affiliation.product_max_price} format='BRL' />
                    </div>
                  ) : (
                    <FormattedNumber value={affiliation.product_price} format='BRL' />
                  )}
                </PermissionHide>
              </Table.Cell>
              <Table.Cell align='right'>
                {affiliation.product_max_comission_charge_type !== 'G' && (
                  <>
                    <PermissionHide inverse action='myeduzz:meus-produtos:ocultar-valores' fallback={<em>oculto</em>}>
                      {affiliation.has_variations && !affiliation.has_differentiated_commission && <>máx: </>}
                      <CalculateCommissionAffiliate
                        price={affiliation.product_price || 0}
                        commissionType={
                          affiliation.has_differentiated_commission
                            ? affiliation.differentiated_comission_type
                            : affiliation.product_max_comission_type
                        }
                        commissionValue={
                          affiliation.has_differentiated_commission ? affiliation.differentiated_comission_value : affiliation.product_max_comission
                        }
                        hasVariations={affiliation.has_variations}
                      />
                    </PermissionHide>
                    &nbsp;de comissão {affiliation.product_max_comission_charge_type === 'A' ? 'recorrente' : null}
                  </>
                )}
                {affiliation.product_max_comission_charge_type === 'G' && (
                  <>
                    <PermissionHide inverse action='myeduzz:meus-produtos:ocultar-valores' fallback={<em>oculto</em>}>
                      <CalculateCommissionAffiliate
                        price={affiliation.product_price ? affiliation.product_price : 0}
                        commissionType={affiliation.comission_type}
                        commissionValue={affiliation.comission}
                      />
                    </PermissionHide>
                    &nbsp;de comissão
                  </>
                )}
              </Table.Cell>
              {affiliation.affiliations_status?.toUpperCase() === EStatusAffiliation.APPROVED && (
                <>
                  <Table.Action onClick={handleClickShare}>Divulgar</Table.Action>

                  {affiliation.recruitment && <Table.Action onClick={() => handleRecruitmentClick(affiliation)}>Recrutar</Table.Action>}
                </>
              )}

              <Table.Action onClick={() => handleClickCancelAffiliation(affiliation)}>Cancelar afiliação</Table.Action>
            </Table.Row>
          ))}
        </Table.Body>
        {!isLoading && pagination && (
          <Table.Pagination
            page={pagination?.current_page}
            perPage={pagination?.per_page}
            onChangePage={onChangePage}
            onChangePerPage={onChangeRowsPerPage}
          />
        )}
      </Table>

      <RecruitmentCommission open={isModalRecruitmentOpen} closeModal={closeModalRecruitment} />
    </>
  );
};
